import React from "react";
import { Spinner } from "react-bootstrap";

// Reusable, consistent action/status control for a WhatsApp template row.
//
// Why this exists: the old inline `outline-success` button rendered an empty
// bordered box (label colour matched the background) in the disabled/submitting
// states, and the PENDING/APPROVED/REJECTED indicators were styled ad-hoc and
// inconsistently. This component renders the WHOLE lifecycle with ALWAYS-VISIBLE
// text and sufficient contrast in every state:
//
//   • no status + not submitting          -> solid "Submit" button
//   • submitting                          -> spinner + visible "Submitting…" label
//   • status PENDING / APPROVED / REJECTED -> a coloured status pill (no button)
//
// The displayed status is driven by `status` (the backend `submissionStatus`
// field) so PENDING survives a page refresh. `submitting` is the local
// optimistic flag used only while a submit request is in flight.
//
// Props:
//   status     : "PENDING" | "APPROVED" | "REJECTED" | null/undefined
//   submitting : boolean — a submit request is currently in flight
//   onSubmit   : () => void — called when the Submit button is pressed
//   disabled   : boolean — externally force-disable the button
//   className  : optional extra class names for the outer control

const STATUS_META = {
  PENDING: { cls: "is-pending", label: "Pending review" },
  APPROVED: { cls: "is-approved", label: "Approved" },
  REJECTED: { cls: "is-rejected", label: "Rejected" },
};

const TemplateActionButton = ({
  status,
  submitting = false,
  onSubmit,
  disabled = false,
  className = "",
}) => {
  const normalized = String(status || "").toUpperCase();
  const statusMeta = STATUS_META[normalized];

  // A resolved backend status takes precedence over the local submit flag, so a
  // PENDING template renders as a pill (and survives refresh) instead of a
  // perpetual spinner.
  if (statusMeta) {
    return (
      <span
        className={`tpl-status-pill ${statusMeta.cls} ${className}`.trim()}
        role="status"
        aria-label={`Template ${statusMeta.label}`}
      >
        {statusMeta.label}
      </span>
    );
  }

  return (
    <button
      type="button"
      className={`tpl-action-btn ${submitting ? "is-submitting" : ""} ${className}`.trim()}
      disabled={disabled || submitting}
      aria-busy={submitting || undefined}
      onClick={onSubmit}
    >
      {submitting ? (
        <>
          <Spinner animation="border" size="sm" className="tpl-action-btn-spinner" />
          <span>Submitting…</span>
        </>
      ) : (
        "Submit"
      )}
    </button>
  );
};

export default TemplateActionButton;
